import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { getBackendUrl } from './get-backend-url';

const BACKEND_ROUTE = getBackendUrl();

/** A run of body copy, a bulleted list, or a table of disclosures. */
type LegalDocumentBlock =
  /** `rows` is rows, then cells, then the lines within a cell. */
  | { headers: string[]; kind: 'table'; rows: string[][][] }
  | { kind: 'paragraph'; text: string }
  | { items: string[]; kind: 'list' };

export type LegalDocumentSection = {
  blocks: LegalDocumentBlock[];
  heading: string;
};

export type LegalDocumentContent = {
  effectiveDate: string;
  sections: LegalDocumentSection[];
};

/** Everything before the first heading. */
const PREAMBLE_HEADING = 'Overview';

const HEADING = /^H([1-6])$/;

/**
 * The lines within one table cell.
 *
 * Cells in these disclosures hold their own bulleted lists, so reading the
 * cell with `textContent` concatenates the items — "Providing the
 * ServicesMarketing the ServicesCorresponding with You". Each item is a line
 * of its own, with any text outside the list kept ahead of them.
 */
const cellLines = (cell: Element): string[] => {
  const items = Array.from(cell.querySelectorAll('li'))
    .map((item) => item.textContent?.trim() ?? '')
    .filter(Boolean);

  if (!items.length) {
    const text = cell.textContent?.trim();

    return text ? [text] : [];
  }

  const withoutLists = cell.cloneNode(true) as Element;
  withoutLists.querySelectorAll('ul, ol').forEach((list) => list.remove());
  const lead = withoutLists.textContent?.trim();

  return lead ? [lead, ...items] : items;
};

/**
 * The level that counts as a section boundary, taken from the first heading
 * in the document.
 *
 * Hardcoding H1/H2 does not survive real documents. The catalog's copy is
 * captured from Webflow pages, and what those emit has already varied: the
 * Terms published in August use `h2`, the Terms as re-captured on 2026-09-01
 * use `h3`, and the Privacy Policy uses `h4` and `h3` as peers. Under a fixed
 * rule the last two collapse into one unreadable section — 165 blocks for the
 * policy — which is the contents view failing at exactly its job.
 *
 * Taking the level from the first heading works because that heading opens the
 * document's top level by construction, whatever depth the CMS assigned it.
 * Anything shallower still counts, so a stray `h2` later on is a boundary too.
 * Deeper headings stay inline as sub-headings, which is what keeps the Terms'
 * `h4` sub-sections attached to the `h3` they belong to instead of being
 * promoted to peers.
 */
const boundaryLevel = (body: HTMLElement): number => {
  for (const node of Array.from(body.children)) {
    const match = HEADING.exec(node.tagName);
    if (match) return Number(match[1]);
  }

  return 1;
};

/**
 * Derives the outline from the document's markup — the catalog stores one
 * HTML string so `content_hash` stays reproducible over stable bytes.
 *
 * Text is read with `textContent`, never injected, so no sanitiser is needed.
 */
export const toSections = (html: string): LegalDocumentSection[] => {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const sections: LegalDocumentSection[] = [];
  const boundary = boundaryLevel(parsed.body);

  let current: LegalDocumentSection = {
    blocks: [],
    heading: PREAMBLE_HEADING,
  };

  for (const node of Array.from(parsed.body.children)) {
    const heading = HEADING.exec(node.tagName);

    if (heading && Number(heading[1]) <= boundary) {
      if (current.blocks.length) sections.push(current);
      current = { blocks: [], heading: node.textContent?.trim() ?? '' };
      continue;
    }

    // A table is read as rows rather than flattened. `textContent` over a
    // table concatenates every cell with no separator, which turned the
    // Privacy Policy's categories-of-data disclosure — a required one — into
    // an unreadable paragraph.
    if (node.tagName === 'TABLE') {
      const rows = Array.from(node.querySelectorAll('tr')).map((row) =>
        Array.from(row.querySelectorAll('th, td')).map(cellLines)
      );

      // The first row is a header only if that row is marked up as one. A
      // table without <th> is data all the way down, and inventing a header
      // would relabel the first disclosure as a column title. Asking the whole
      // table would do exactly that whenever a <th> appears further down.
      const hasHeader = !!node.querySelector('tr')?.querySelector('th');
      const headers = hasHeader
        ? rows[0].map((lines) => lines.join(' '))
        : [];
      const body = (hasHeader ? rows.slice(1) : rows).filter((row) =>
        row.some((lines) => lines.length)
      );

      if (body.length) {
        current.blocks.push({ headers, kind: 'table', rows: body });
      }
      continue;
    }

    if (node.tagName === 'UL' || node.tagName === 'OL') {
      const items = Array.from(node.children)
        .map((item) => item.textContent?.trim() ?? '')
        .filter(Boolean);

      if (items.length) current.blocks.push({ items, kind: 'list' });
      continue;
    }

    const text = node.textContent?.trim();
    if (text) current.blocks.push({ kind: 'paragraph', text });
  }

  if (current.blocks.length) sections.push(current);

  return sections;
};

/**
 * Fetches the published text, hash-verified server-side.
 *
 * Fetched on open rather than inlined in `/auth/validate`, since most
 * employers never open one. Not retried — the caller falls back to the link.
 */
export const useLegalDocumentContent = (legalDocumentId: string | null) =>
  useQuery({
    enabled: !!legalDocumentId,
    queryFn: async (): Promise<LegalDocumentContent | null> => {
      if (!legalDocumentId) return null;

      const { data } = await axios.post(
        `${BACKEND_ROUTE}/auth/legal-document-content`,
        { legalDocumentId },
      );

      return {
        effectiveDate: data.versionLabel,
        sections: toSections(data.content),
      };
    },
    queryKey: ['legal-document-content', legalDocumentId],
    refetchOnWindowFocus: false,
    retry: false,
  });
